import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, MapPin, ArrowUpRight, ArrowLeft } from "lucide-react";
import Seo from "@/components/site/Seo";
import { CTABand } from "@/pages/BusinessWaste";
import { BLOG_POSTS } from "@/lib/blog";
import { useI18n } from "@/i18n";

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang, localePath } = useI18n();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to={localePath("/blog")} replace />;
  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 3);
  const fallback = related.length ? related : BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org", "@type": "BlogPosting",
    headline: post.title, image: `https://eewaste.com${post.cover}`, datePublished: post.date, dateModified: post.date,
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://eewaste.com/blog/${post.slug}` },
    author: { "@type": "Organization", name: "EWS" },
    publisher: {
      "@type": "Organization",
      name: "EWS",
      logo: { "@type": "ImageObject", url: "https://eewaste.com/img/ews-logo.jpg" },
    },
    description: post.excerpt,
  };

  return (
    <>
      <Seo title={`${post.title} | EWS Miami`} description={post.excerpt} image={post.cover} path={`/blog/${post.slug}`} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative bg-[#0E3B3B] text-white pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle at 80% 15%, rgba(16,185,129,0.5), transparent 45%)" }} />
        <div className="relative z-[2] max-w-3xl mx-auto px-5 sm:px-8">
          <Link to={localePath("/blog")} className="inline-flex items-center gap-1.5 font-mono-tech text-[11px] uppercase tracking-widest text-emerald-300 hover:text-emerald-200 mb-6"><ArrowLeft size={14} /> {t("bp.allArticles")}</Link>
          <div className="flex items-center gap-4 font-mono-tech text-[11px] uppercase tracking-widest text-white/60 mb-4">
            <span className="text-emerald-400">{t(`cat.${post.category}`)}</span>
            <span className="flex items-center gap-1"><MapPin size={12} /> {post.city}</span>
            <span className="flex items-center gap-1"><Clock size={12} /> {post.read}</span>
            <span>{new Date(post.date).toLocaleDateString(lang === "es" ? "es-US" : "en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
          </div>
          <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">{post.title}</h1>
        </div>
      </section>

      <section className="relative bg-[#FBFAF8] py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="rounded-3xl overflow-hidden border border-[#E7E5DF] shadow-soft mb-10 aspect-[16/9]">
            <img loading="lazy" decoding="async" src={post.cover} alt={post.title} className="w-full h-full object-cover" />
          </div>
          <p className="text-lg text-[#0B0F14] font-medium leading-relaxed mb-10">{post.excerpt}</p>
          <article className="space-y-10">
            {post.body.map((sec, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                <h2 className="font-display font-extrabold text-2xl text-[#0B0F14] mb-4">{sec.h}</h2>
                {sec.p.map((para, j) => <p key={j} className="text-slate-600 leading-relaxed mb-4">{para}</p>)}
              </motion.div>
            ))}
          </article>

          <div className="mt-12 rounded-2xl bg-[#0E3B3B] text-white p-8 text-center">
            <h3 className="font-display font-bold text-xl">{t("bp.readyTitle")} {post.city}?</h3>
            <p className="text-white/70 text-sm mt-2">{t("bp.readyBody")}</p>
            <Link to={localePath("/quick-quote")} className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold hover:bg-emerald-400 transition-all">{t("bp.getQuote")}</Link>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="relative bg-[#F0EFEA] py-20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0B0F14] mb-8">{t("bp.keepReading")}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {fallback.map((p) => (
              <Link key={p.slug} to={localePath(`/blog/${p.slug}`)} data-testid={`related-${p.slug}`}
                className="group bg-white border border-[#E7E5DF] rounded-2xl overflow-hidden shadow-soft hover:-translate-y-1 transition-all">
                <div className="h-36 overflow-hidden"><img loading="lazy" decoding="async" src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <div className="p-5">
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-emerald-700">{t(`cat.${p.category}`)}</span>
                  <h3 className="mt-2 font-display font-bold text-[#0B0F14] leading-snug group-hover:text-emerald-700 transition-colors">{p.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B0F14]">{t("bp.read")} <ArrowUpRight size={15} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
